/**
 * Regtank liveness verification hook
 * Handles liveness URL generation, status checking, and verification flow
 *
 * Refactored to use shared document signing hooks for consistency:
 * - useRegtankLivenessSession: Manages session extraction and liveness data
 * - useRegtankLivenessValidation: Handles validation logic (canAttempt, shouldVerify, isDisabled)
 */

import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { isValidRegtankUrl } from "@funded-here-interface/common/src/Utils/urlValidation";
import { LivenessWebVerifyStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { setRegtankLiveness } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { MESSAGES, useButtonState } from "../../../components/DocumentSigning";
import useGetLivenessWebVerifyLink from "../../../hooks/useGetLivenessWebVerifyLink";
import useGetRegtankLivenessWebVerifyStatus from "../../../hooks/useGetRegtankLivenessWebVerifyStatus";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUser";
import { useRegtankLivenessSession } from "../../../hooks/useRegtankLivenessSession";
import { useRegtankLivenessValidation } from "../../../hooks/useRegtankLivenessValidation";
import { saveSubscriptionToSession } from "../utils/sessionStorage";
import { DOCUMENT_CONFIG, TOAST_IDS } from "../StructureNote.constants";
import { LivenessVerifyParams } from "../StructureNote.types";

interface UseRegtankLivenessProps {
  token: string;
  documentId: number;
  regtankLivenessFeatureFlag: boolean;
  regtankOnboardingFeatureFlag: boolean | null;
  regtankOnboarding: boolean | null;
  regtankRequest: any;
  regtankLiveness?: any[];
  onSignSuccess: () => void;
  onViewerKeyIncrement: () => void;
}

/**
 * Hook to manage Regtank liveness verification flow
 * Now uses shared hooks for session management and validation
 *
 * @param props - Hook configuration
 * @returns Liveness state and handler functions
 */
export const useRegtankLiveness = ({
  token,
  documentId,
  regtankLivenessFeatureFlag,
  regtankOnboardingFeatureFlag,
  regtankOnboarding,
  regtankRequest,
  regtankLiveness,
  onSignSuccess,
  onViewerKeyIncrement,
}: UseRegtankLivenessProps) => {
  const [regtankLivenessUrl, setRegtankLivenessUrl] = useState("");
  const [showFailedModal, setShowFailedModal] = useState(false);

  const dispatch = useDispatch();
  const livenessLinkMutation = useGetLivenessWebVerifyLink();
  const livenessStatusMutation = useGetRegtankLivenessWebVerifyStatus();
  const { data: userData } = useGetUser(token);
  const redirectUrl = window.location.href;

  // Use shared hooks for session management and validation
  const allLivenessData = userData?.user?.regtankLiveness || regtankLiveness;
  const livenessSession = useRegtankLivenessSession(
    documentId,
    allLivenessData
  );
  const livenessValidation = useRegtankLivenessValidation(livenessSession);

  // Use shared button state hook for consistent button disabled/title logic
  const { buttonDisabled, titleButton, disableButton } = useButtonState({
    regtankLivenessFeatureFlag,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
    regtankRequest,
    isDisabledByLiveness: livenessValidation.isDisabled,
  });

  /**
   * Generate liveness verification URL
   */
  const generateLivenessUrl = useCallback(
    (params: LivenessVerifyParams) => {
      toast.info(MESSAGES.LOADING_LIVENESS, {
        toastId: TOAST_IDS.LOADING_LIVENESS,
      });

      livenessLinkMutation.mutate(
        {
          token,
          documentId,
          redirectUrl,
          agreementName: DOCUMENT_CONFIG.AGREEMENT_NAME,
        },
        {
          onSuccess: (data) => {
            if (!isValidRegtankUrl(data.verifyLink)) {
              toast.error(MESSAGES.INVALID_URL);
              onViewerKeyIncrement();
              return;
            }

            disableButton(MESSAGES.VERIFICATION_DISABLED);

            // Save subscription state to session storage
            saveSubscriptionToSession({
              category: "subscribe",
              realUrl: params.realUrl,
              documentId,
              completedAt: params.completedAt,
              investorSubscribeAmount: params.investorSubscribeAmount,
              selectedData: params.selectedData,
              sessionId: data.sessionId,
            });

            // Update Redux state
            dispatch(
              setRegtankLiveness({
                regtankLiveness: [
                  {
                    id: documentId.toString(),
                    clientSessionId: documentId.toString(),
                    sessionId: data.sessionId,
                    livenessStatus: null,
                    regtankLivenessUrl: data.verifyLink,
                    noOfAttemptLiveness: 0,
                    reattemptApproval: null,
                    expiredDate: data.expiredDate,
                  },
                ],
              })
            );

            window.open(data.verifyLink, "_self");
            onViewerKeyIncrement();
          },
          onError: (e) => {
            toast.error((e as Error).message);
            onViewerKeyIncrement();
          },
        }
      );
    },
    [
      token,
      documentId,
      redirectUrl,
      dispatch,
      livenessLinkMutation,
      onViewerKeyIncrement,
      disableButton,
    ]
  );

  /**
   * Main liveness verification handler
   */
  const handleLivenessVerify = useCallback(
    (params: LivenessVerifyParams) => {
      if (!regtankLivenessFeatureFlag) {
        onSignSuccess();
        return;
      }

      // Use existing liveness URL if available
      if (regtankLivenessUrl) {
        if (!isValidRegtankUrl(regtankLivenessUrl)) {
          toast.error(MESSAGES.INVALID_URL);
          return;
        }
        window.open(regtankLivenessUrl, "_self");
      } else {
        generateLivenessUrl(params);
      }
    },
    [
      regtankLivenessFeatureFlag,
      regtankLivenessUrl,
      generateLivenessUrl,
      onSignSuccess,
    ]
  );

  /**
   * Handle modal close after failed liveness
   */
  const handleModalClose = useCallback(() => {
    disableButton(MESSAGES.VERIFICATION_DISABLED);
    setShowFailedModal(false);
    sessionStorage.removeItem("regtankSessionId");
    onViewerKeyIncrement();
  }, [disableButton, onViewerKeyIncrement]);

  // Verify liveness status when needed (using shared validation hook)
  useEffect(() => {
    if (
      !regtankLivenessFeatureFlag ||
      !livenessSession ||
      !livenessValidation.shouldVerify
    ) {
      return;
    }

    livenessStatusMutation.mutate(
      { token, sessionId: livenessSession.sessionId },
      {
        onSuccess: (data: any) => {
          if (data === LivenessWebVerifyStatusEnum.SUCCESS) {
            onSignSuccess();
          } else if (
            data === LivenessWebVerifyStatusEnum.LIVENESS_FAILED ||
            data === LivenessWebVerifyStatusEnum.FAILED
          ) {
            setShowFailedModal(true);
          }
          onViewerKeyIncrement();
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    livenessSession,
    livenessValidation.shouldVerify,
    token,
    regtankLivenessFeatureFlag,
  ]);

  // Set liveness URL when user can attempt verification (using shared validation hook)
  useEffect(() => {
    if (livenessValidation.canAttempt && livenessSession?.regtankLivenessUrl) {
      setRegtankLivenessUrl(livenessSession.regtankLivenessUrl);
    }
  }, [livenessValidation.canAttempt, livenessSession]);

  return {
    buttonDisabled,
    titleButton,
    showFailedModal,
    regtankLivenessUrl,
    handleLivenessVerify,
    handleModalClose,
    isLoading:
      livenessLinkMutation.isLoading || livenessStatusMutation.isLoading,
  };
};
